import React from "react";
import { REMOTE_URLS } from "../../../constants";
import { loadRemoteModule } from "../../../util/loadRemoteModule";
import { MODE_QUESTION_ONLY, TOOL_CONSUMER_BRONTE } from "./constants";
import { prepareFitConfig } from "./prepareFitConfig";

const WSOL_SCOPE = "remote";
const WSOL_MODULE = "FitIntegrator";
const SITE_ID = "6";

export type JournalQuestionOnlyParams = {
  mode: typeof MODE_QUESTION_ONLY;
  content_id: string;
  tool_consumer: string;
  component_id: string;
};

type FitInitializer = {
  setConfig: (consumer: string, config: unknown, reload: boolean) => void;
};

declare global {
  interface Window {
    FIT?: { Initializer?: FitInitializer };
    __FIT_INITIALIZED__?: boolean;
  }
}

function normalizeEnv(env: string | undefined): string {
  if (env === "qa") {
    return "qaint";
  }
  if (env === "prd") {
    return "prod";
  }
  return env ?? "stg";
}

function getRemoteConfig(env: string | undefined) {
  const key = normalizeEnv(env);
  return REMOTE_URLS[key] ?? REMOTE_URLS.stg;
}

export function buildJournalToolsConfig(env: string | undefined) {
  const { WSOL, fitjsUrl, commonIesClientId } = getRemoteConfig(env);

  return prepareFitConfig({
    commonFitProps: {
      siteId: SITE_ID,
      commonIesClientId,
      fitjsUrl,
    },
    workflowSettings: {
      PLAY_WSOL: {
        appName: WSOL_SCOPE,
        launchMode: "WEB_COMPONENT",
        moduleName: WSOL_MODULE,
        url: WSOL,
        env: normalizeEnv(env),
      },
    },
  });
}

async function loadFitScript(fitjsUrl: string): Promise<void> {
  if (window.FIT?.Initializer) {
    return;
  }

  const existing = Array.from(document.head.getElementsByTagName("script")).some(
    (script) => script.src === fitjsUrl,
  );
  if (existing) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = fitjsUrl;
    script.id = "fit-js-script";
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load FIT script: ${fitjsUrl}`));
    document.head.appendChild(script);
  });
}

export function resolveHostAuthToken(
  getAuthToken?: (callback: (token: string) => void) => void,
): Promise<string | null> {
  if (!getAuthToken) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    getAuthToken((token) => {
      resolve(token?.trim() ? token : null);
    });
  });
}

export function initializeJournalFit(
  toolsConfig: ReturnType<typeof buildJournalToolsConfig>,
  onInitialized: (initialized: boolean) => void,
): void {
  if (window.__FIT_INITIALIZED__) {
    onInitialized(true);
    return;
  }

  if (window.FIT?.Initializer) {
    window.FIT.Initializer.setConfig("BRONTE", toolsConfig, false);
    window.__FIT_INITIALIZED__ = true;
    onInitialized(true);
    return;
  }

  console.error("FIT SDK is not available. Make sure the FIT script is loaded.");
  onInitialized(false);
}

export async function loadJournalFitScript(env: string | undefined): Promise<void> {
  const { fitjsUrl } = getRemoteConfig(env);
  await loadFitScript(fitjsUrl);
}

export function buildQuestionOnlyParams(
  itemId: string,
  componentId: string,
): JournalQuestionOnlyParams {
  return {
    mode: MODE_QUESTION_ONLY,
    content_id: itemId,
    tool_consumer: TOOL_CONSUMER_BRONTE,
    component_id: componentId,
  };
}

export function createRemoteJournalWsol(env: string | undefined) {
  const { WSOL } = getRemoteConfig(env);

  return React.lazy(() =>
    loadRemoteModule(WSOL, WSOL_SCOPE, `./${WSOL_MODULE}`).then((mod) => ({
      default: mod.default || mod,
    })),
  ) as React.LazyExoticComponent<
    React.ComponentType<{ params: JournalQuestionOnlyParams }>
  >;
}
